import "@/App.css";
import { useEffect, useState, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, Link } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { UserProfileProvider, useUserProfile } from "@/contexts/UserProfileContext";
import { initializeGTM, trackVirtualPageView } from "@/services/gtmService";
import { subscribeToCoreServicesConfig } from "@/services/coreServicesClient";
import { captureInviteParams } from "@/services/b2bInviteService";
import { db } from "@/firebase";
import { doc, getDoc } from "firebase/firestore";
import Login from "@/pages/Login";
import Onboarding from "@/pages/Onboarding";
import FormF1 from "@/pages/FormF1";
import FormF2 from "@/pages/FormF2";
import FormF3 from "@/pages/FormF3";
import FormF4 from "@/pages/FormF4";
import FormF5 from "@/pages/FormF5";
import Recommendations from "@/pages/Recommendations";
import ClientConsole from "@/pages/Client";
import Changelog from "@/pages/Changelog";
import DevApiTester from "@/pages/DevApiTester";
import Dashboard from "@/pages/Dashboard";
import Measurement from "@/pages/Measurement";
import MiCuenta from "@/pages/MiCuenta";
import ExportPreview from "@/pages/MiCuenta/ExportPreview";
import VerifyEmail from "@/pages/VerifyEmail";
import CommitteeRulesVote from "@/pages/CommitteeRulesVote";
import AdminPanel from "@/pages/Admin";
import Forbidden from "@/pages/Forbidden";
import IHODashboard from "@/pages/IHODashboard";
import DoctorDashboard from "@/pages/DoctorDashboard";
import DemoBanner from "@/components/DemoBanner";
import ImpersonationBanner from "@/components/ImpersonationBanner";
import LinkAccountModal from "@/components/LinkAccountModal";
import FhirInspectorDrawer from "@/components/we360/FhirInspectorDrawer";
import { Loader2, User, ShieldCheck, Building2, ShieldAlert, Menu, X as XIcon, Home, Camera, Database, ChevronDown, LogOut, Activity, Sparkles } from "lucide-react";


// Loading screen component
function LoadingScreen() {
  return (
    <div 
      className="min-h-screen flex items-center justify-center"
      style={{ background: "#FCFAEE" }}
    >
      <div className="text-center">
        <Loader2 
          size={40} 
          className="animate-spin mx-auto mb-4" 
          style={{ color: "#00628F" }}
        />
        <p className="text-sm" style={{ color: "#71644C" }}>
          Cargando...
        </p>
      </div>
    </div>
  );
}

// Protected route wrapper
function ProtectedRoute({ children }) {
  const { user, loading: authLoading } = useAuth();
  const { loading: profileLoading, isBlocked } = useUserProfile();

  if (authLoading || profileLoading) {
    return <LoadingScreen />;
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  if (isBlocked) {
    return (
      <div
        className="min-h-screen flex items-center justify-center px-4"
        style={{ background: "#FCFAEE" }}
        data-testid="blocked-page"
      >
        <div className="text-center max-w-md">
          <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6" style={{ background: "#FEE2E2" }}>
            <User size={32} style={{ color: "#B91C1C" }} />
          </div>
          <h2 className="text-lg font-bold mb-3" style={{ color: "#1E293B" }}>Cuenta desactivada</h2>
          <p className="text-sm mb-6" style={{ color: "#71644C" }}>
            Tu cuenta ha sido desactivada por un administrador.
            Contacta al equipo de WE360 para más información.
          </p>
        </div>
      </div>
    );
  }

  return children;
}

// Admin route wrapper — returns 403 if not admin
function AdminRoute({ children }) {
  const { user, loading: authLoading } = useAuth();
  const { loading: profileLoading, isAdmin } = useUserProfile();

  if (authLoading || profileLoading) {
    return <LoadingScreen />;
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  if (!isAdmin) {
    return <Forbidden />;
  }

  return children;
}

// Medico route wrapper — returns 403 if not medico or admin
function MedicoRoute({ children }) {
  const { user, loading: authLoading } = useAuth();
  const { loading: profileLoading, isMedico, isAdmin } = useUserProfile();

  if (authLoading || profileLoading) {
    return <LoadingScreen />;
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  if (!isMedico && !isAdmin) {
    return <Forbidden />;
  }

  return children;
}

// User header component showing logged in user (not shown for demo users)
function UserHeader() {
  const { user, logout } = useAuth();
  const { profile, isAdmin, isClientAdmin, personalEmail, clientId, isIHO, isMedico, openFhirDrawer } = useUserProfile();
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const location = useLocation();

  // Cerrar el menú cuando cambia la ruta
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // Cerrar el menú al hacer clic fuera o presionar Escape
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setMenuOpen(false);
      }
    };
    if (menuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleKeyDown);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [menuOpen]);

  if (!user || user.isDemo) return null;

  const handleLogout = async () => {
    setMenuOpen(false);
    await logout();
  };

  // Etiqueta de rol principal para el pill de la cabecera
  const getPrimaryRoleBadge = () => {
    if (user.isAnonymous) return "Invitado";
    if (isAdmin) return "Admin";
    if (isMedico) return "Médico";
    if (isIHO) return "IHO";
    if (isClientAdmin) return "Empresa";
    return "Colaborador";
  };

  const primaryRoleLabel = getPrimaryRoleBadge();

  const headerBg = user.isAnonymous
    ? "linear-gradient(135deg, #F9A01B 0%, #F97316 100%)"
    : "linear-gradient(135deg, #00628F 0%, #0080A0 50%, #2A9D8F 100%)";

  return (
    <>
      <header
        style={{
          background: headerBg,
          color: "white",
          fontFamily: "Inter, sans-serif",
          position: "relative",
          zIndex: 50,
        }}
        className="px-4 py-2.5 sm:px-6 shadow-sm border-b border-white/10"
        data-testid="user-header"
      >
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          {/* LADO IZQUIERDO: Marca WE360 con enlace a Dashboard */}
          <Link
            to="/dashboard"
            className="flex items-center gap-2.5 text-white hover:opacity-90 transition-opacity group select-none"
            style={{ textDecoration: "none" }}
            title="Ir al Inicio de WE360"
          >
            <div className="w-8 h-8 rounded-xl bg-white/15 backdrop-blur-md border border-white/25 flex items-center justify-center shadow-sm group-hover:scale-105 transition-transform">
              <Activity size={17} className="text-white" />
            </div>
            <div className="flex items-center gap-1.5 notranslate" translate="no">
              <span className="font-black text-base tracking-tight leading-none text-white notranslate" translate="no">
                WE<span className="text-teal-200">360</span>
              </span>
              <span className="hidden xs:inline-block text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-white/15 text-white/90 border border-white/20 notranslate" translate="no">
                Salud
              </span>
            </div>
          </Link>

          {/* LADO DERECHO: Pill interactivo del Perfil de Usuario */}
          <div ref={menuRef} className="relative">
            <button
              type="button"
              onClick={() => setMenuOpen((prev) => !prev)}
              aria-expanded={menuOpen}
              aria-haspopup="true"
              aria-label="Menú de usuario"
              data-testid="mobile-menu-btn"
              className="flex items-center gap-2 sm:gap-2.5 pl-1.5 pr-2.5 py-1 rounded-full bg-white/15 hover:bg-white/25 active:bg-white/30 backdrop-blur-md border border-white/25 transition-all text-white shadow-sm cursor-pointer select-none group"
            >
              {/* Avatar */}
              {user.photoURL ? (
                <img
                  src={user.photoURL}
                  alt=""
                  className="w-6 h-6 rounded-full object-cover shrink-0 ring-1 ring-white/50"
                  referrerPolicy="no-referrer"
                />
              ) : (
                <div
                  className="w-6 h-6 rounded-full flex items-center justify-center shrink-0 ring-1 ring-white/40"
                  style={{ background: "rgba(255,255,255,0.3)" }}
                >
                  <User size={13} className="text-white" />
                </div>
              )}

              {/* Nombre de usuario */}
              <span className="text-xs font-semibold truncate max-w-[110px] sm:max-w-[180px] tracking-tight">
                {user.isAnonymous
                  ? "Invitado"
                  : (user.displayName || profile?.nombre || user.email?.split("@")[0] || "Mi Perfil")}
              </span>

              {/* Badge de Rol */}
              <span className="hidden sm:inline-flex text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-white/20 text-white/95 border border-white/30 uppercase tracking-wide">
                {primaryRoleLabel}
              </span>

              {/* Indicador de acción pendiente (correo personal sin asociar a empresa) */}
              {clientId && !personalEmail && (
                <span
                  className="w-2 h-2 rounded-full bg-amber-300 ring-2 ring-teal-800 shrink-0 animate-pulse"
                  title="Acción pendiente en tu perfil"
                />
              )}

              {/* Flecha desplegable animada */}
              <ChevronDown
                size={14}
                className={`text-white/80 transition-transform duration-200 shrink-0 ${
                  menuOpen ? "rotate-180 text-white" : "group-hover:translate-y-0.5"
                }`}
              />
            </button>

            {/* MENÚ DESPLEGABLE FLOTANTE */}
            {menuOpen && (
              <>
                {/* Overlay difuminado para cerrar al hacer clic fuera */}
                <div
                  className="fixed inset-0 z-40 bg-slate-900/20 backdrop-blur-[1px] transition-opacity"
                  onClick={() => setMenuOpen(false)}
                  aria-hidden="true"
                />

                {/* Contenedor Dropdown Premium Glassmorphism */}
                <div
                  className="absolute right-0 top-full mt-2 z-50 w-72 sm:w-80 rounded-2xl bg-white/95 backdrop-blur-xl border border-slate-200/90 shadow-[0_20px_50px_rgba(0,0,0,0.18)] overflow-hidden transition-all text-slate-800"
                  data-testid="mobile-menu-dropdown"
                >
                  {/* Encabezado del usuario */}
                  <div className="p-3.5 bg-gradient-to-br from-slate-50 to-teal-50/50 border-b border-slate-100 flex items-start gap-3">
                    {user.photoURL ? (
                      <img
                        src={user.photoURL}
                        alt=""
                        className="w-10 h-10 rounded-full object-cover shrink-0 ring-2 ring-teal-500/20"
                        referrerPolicy="no-referrer"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 bg-teal-600/10 text-teal-700 font-bold ring-2 ring-teal-500/20">
                        <User size={18} />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-bold text-slate-900 truncate leading-snug">
                        {user.isAnonymous
                          ? "Usuario Invitado"
                          : (user.displayName || profile?.nombre || "Usuario")}
                      </p>
                      <p className="text-xs text-slate-500 truncate mt-0.5">
                        {user.isAnonymous ? "Sesión temporal de invitado" : user.email}
                      </p>
                      <div className="flex flex-wrap items-center gap-1.5 mt-2">
                        {isAdmin && (
                          <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 border border-purple-200">
                            Super Admin
                          </span>
                        )}
                        {isMedico && (
                          <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-200">
                            Médico Clínico
                          </span>
                        )}
                        {isIHO && (
                          <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-200">
                            Oficial IHO
                          </span>
                        )}
                        {isClientAdmin && (
                          <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-sky-100 text-sky-700 border border-sky-200">
                            Gestión Empresa
                          </span>
                        )}
                        {!isAdmin && !isMedico && !isIHO && !isClientAdmin && (
                          <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200">
                            Colaborador
                          </span>
                        )}
                        {clientId && !personalEmail && (
                          <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-300 flex items-center gap-1">
                            <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                            Vincular correo
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Botón vincular cuenta anónima */}
                  {user.isAnonymous && (
                    <div className="p-2.5 bg-amber-50/80 border-b border-amber-200/60">
                      <button
                        type="button"
                        onClick={() => {
                          setMenuOpen(false);
                          setShowLinkModal(true);
                        }}
                        data-testid="upgrade-btn"
                        className="w-full flex items-center justify-center gap-2 px-3 py-2 text-xs font-bold text-amber-900 bg-amber-200/80 hover:bg-amber-300/80 rounded-xl transition-colors cursor-pointer"
                      >
                        <Sparkles size={14} className="text-amber-700" />
                        <span>Vincular cuenta oficial</span>
                      </button>
                    </div>
                  )}

                  {/* Lista de navegación agrupada por módulos */}
                  <div className="p-2 space-y-3 max-h-[calc(100vh-160px)] overflow-y-auto">
                    {/* MÓDULO 1: MI SALUD & BIENESTAR */}
                    <div>
                      <div className="px-2.5 py-1 text-[10px] font-bold tracking-wider text-slate-400 uppercase">
                        Mi Salud & Bienestar
                      </div>
                      <div className="space-y-0.5">
                        <Link
                          to="/dashboard"
                          onClick={() => setMenuOpen(false)}
                          data-testid="dashboard-link"
                          className="flex items-center gap-3 px-2.5 py-2 rounded-xl text-slate-700 hover:text-slate-900 hover:bg-slate-100/80 transition-colors group"
                          style={{ textDecoration: "none" }}
                        >
                          <div className="w-7 h-7 rounded-lg bg-teal-50 text-teal-600 flex items-center justify-center group-hover:bg-teal-100 transition-colors shrink-0">
                            <Home size={15} />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="text-xs font-semibold leading-tight">Inicio</div>
                            <div className="text-[11px] text-slate-400 leading-tight">Resumen y biomarcadores</div>
                          </div>
                        </Link>

                        <Link
                          to="/medicion/nueva"
                          onClick={() => setMenuOpen(false)}
                          data-testid="nueva-medicion-link"
                          className="flex items-center gap-3 px-2.5 py-2 rounded-xl text-slate-700 hover:text-slate-900 hover:bg-slate-100/80 transition-colors group"
                          style={{ textDecoration: "none" }}
                        >
                          <div className="w-7 h-7 rounded-lg bg-teal-50 text-teal-600 flex items-center justify-center group-hover:bg-teal-100 transition-colors shrink-0">
                            <Camera size={15} />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="text-xs font-semibold leading-tight">Nueva medición</div>
                            <div className="text-[11px] text-slate-400 leading-tight">Face scan rPPG o manual</div>
                          </div>
                        </Link>

                        {!user.isAnonymous && (
                          <Link
                            to="/mi-cuenta"
                            onClick={() => setMenuOpen(false)}
                            data-testid="mi-cuenta-link"
                            className="flex items-center gap-3 px-2.5 py-2 rounded-xl text-slate-700 hover:text-slate-900 hover:bg-slate-100/80 transition-colors group"
                            style={{ textDecoration: "none" }}
                          >
                            <div className="w-7 h-7 rounded-lg bg-teal-50 text-teal-600 flex items-center justify-center group-hover:bg-teal-100 transition-colors shrink-0">
                              <User size={15} />
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="text-xs font-semibold leading-tight flex items-center gap-1.5">
                                <span>Mi cuenta</span>
                                {clientId && !personalEmail && (
                                  <span className="w-2 h-2 rounded-full bg-amber-400 ring-1 ring-amber-200" />
                                )}
                              </div>
                              <div className="text-[11px] text-slate-400 leading-tight">Perfil y preferencias</div>
                            </div>
                          </Link>
                        )}
                      </div>
                    </div>

                    {/* MÓDULO 2: GESTIÓN EMPRESARIAL */}
                    {isClientAdmin && (
                      <div>
                        <div className="px-2.5 py-1 text-[10px] font-bold tracking-wider text-slate-400 uppercase">
                          Gestión Empresarial
                        </div>
                        <div className="space-y-0.5">
                          <Link
                            to="/cliente"
                            onClick={() => setMenuOpen(false)}
                            data-testid="client-console-link"
                            className="flex items-center gap-3 px-2.5 py-2 rounded-xl text-slate-700 hover:text-slate-900 hover:bg-slate-100/80 transition-colors group"
                            style={{ textDecoration: "none" }}
                          >
                            <div className="w-7 h-7 rounded-lg bg-sky-50 text-sky-600 flex items-center justify-center group-hover:bg-sky-100 transition-colors shrink-0">
                              <Building2 size={15} />
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="text-xs font-semibold leading-tight">Mi empresa / SST</div>
                              <div className="text-[11px] text-slate-400 leading-tight">Salud ocupacional corporativa</div>
                            </div>
                          </Link>
                        </div>
                      </div>
                    )}

                    {/* MÓDULO 3: MÓDULO CLÍNICO & VIGILANCIA */}
                    {(isMedico || isIHO || isAdmin) && (
                      <div>
                        <div className="px-2.5 py-1 text-[10px] font-bold tracking-wider text-slate-400 uppercase">
                          Módulo Clínico & Vigilancia
                        </div>
                        <div className="space-y-0.5">
                          {(isMedico || isAdmin) && (
                            <Link
                              to="/medico"
                              onClick={() => setMenuOpen(false)}
                              data-testid="medico-dashboard-link"
                              className="flex items-center gap-3 px-2.5 py-2 rounded-xl text-slate-700 hover:text-slate-900 hover:bg-slate-100/80 transition-colors group"
                              style={{ textDecoration: "none" }}
                            >
                              <div className="w-7 h-7 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center group-hover:bg-emerald-100 transition-colors shrink-0">
                                <ShieldCheck size={15} />
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="text-xs font-semibold leading-tight">Portal Clínico</div>
                                <div className="text-[11px] text-slate-400 leading-tight">Vigilancia, Clústeres & Triaje</div>
                              </div>
                            </Link>
                          )}

                          {isIHO && (
                            <Link
                              to="/iho"
                              onClick={() => setMenuOpen(false)}
                              data-testid="iho-dashboard-link"
                              className="flex items-center gap-3 px-2.5 py-2 rounded-xl text-slate-700 hover:text-slate-900 hover:bg-slate-100/80 transition-colors group"
                              style={{ textDecoration: "none" }}
                            >
                              <div className="w-7 h-7 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center group-hover:bg-amber-100 transition-colors shrink-0">
                                <ShieldAlert size={15} />
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="text-xs font-semibold leading-tight">Monitor IHO</div>
                                <div className="text-[11px] text-slate-400 leading-tight">Oficial de Higiene Ocupacional</div>
                              </div>
                            </Link>
                          )}
                        </div>
                      </div>
                    )}

                    {/* MÓDULO 4: ADMINISTRACIÓN & AUDITORÍA */}
                    {isAdmin && (
                      <div>
                        <div className="px-2.5 py-1 text-[10px] font-bold tracking-wider text-slate-400 uppercase">
                          Administración & Auditoría
                        </div>
                        <div className="space-y-0.5">
                          <Link
                            to="/admin"
                            onClick={() => setMenuOpen(false)}
                            data-testid="admin-link"
                            className="flex items-center gap-3 px-2.5 py-2 rounded-xl text-slate-700 hover:text-slate-900 hover:bg-slate-100/80 transition-colors group"
                            style={{ textDecoration: "none" }}
                          >
                            <div className="w-7 h-7 rounded-lg bg-purple-50 text-purple-600 flex items-center justify-center group-hover:bg-purple-100 transition-colors shrink-0">
                              <ShieldCheck size={15} />
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="text-xs font-semibold leading-tight">Panel de Administración</div>
                              <div className="text-[11px] text-slate-400 leading-tight">Gestión global y empresas</div>
                            </div>
                          </Link>

                          <button
                            type="button"
                            onClick={() => {
                              setMenuOpen(false);
                              openFhirDrawer();
                            }}
                            data-testid="admin-fhir-console-btn"
                            className="w-full flex items-center gap-3 px-2.5 py-2 rounded-xl text-slate-700 hover:text-slate-900 hover:bg-slate-100/80 transition-colors text-left group cursor-pointer"
                          >
                            <div className="w-7 h-7 rounded-lg bg-teal-50 text-teal-600 flex items-center justify-center group-hover:bg-teal-100 transition-colors shrink-0">
                              <Database size={15} />
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="text-xs font-semibold leading-tight flex items-center gap-1.5">
                                <span>Consola FHIR</span>
                                <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.2 rounded bg-teal-100 text-teal-800">
                                  GCP R4
                                </span>
                              </div>
                              <div className="text-[11px] text-slate-400 leading-tight">Auditoría de interoperabilidad</div>
                            </div>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* CIERRE DE SESIÓN */}
                  <div className="p-2 border-t border-slate-100 bg-slate-50/70">
                    <button
                      type="button"
                      onClick={handleLogout}
                      data-testid="logout-btn"
                      className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-xs font-semibold text-rose-700 hover:bg-rose-50 hover:text-rose-800 transition-colors cursor-pointer"
                    >
                      <LogOut size={15} className="text-rose-600 shrink-0" />
                      <span>Cerrar sesión</span>
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </header>

      {showLinkModal && (
        <LinkAccountModal onClose={() => setShowLinkModal(false)} defaultEmail={profile?.email || ""} />
      )}
    </>
  );
}

// Main app content with routes
function AppContent() {
  const { user, loading } = useAuth();
  const {
    profile,
    loading: profileLoading,
    onboardingCompleted,
    isImpersonating,
    isMedico,
    isAdmin,
    fhirDrawerOpen,
    closeFhirDrawer,
    openFhirDrawer,
  } = useUserProfile();



  const location = useLocation();
  const [gtmId, setGtmId] = useState("");
  const [gtmLoaded, setGtmLoaded] = useState(false);
  const lastTrackedPath = useRef("");

  // Captura parámetros de invitación B2B (?cid=, ?empresa=, ?email=) en cualquier ruta
  useEffect(() => {
    captureInviteParams();
  }, [location.search]);

  useEffect(() => {
    async function loadGTMConfig() {
      try {
        const snap = await getDoc(doc(db, "config", "general"));
        if (snap.exists() && snap.data().gtmId) {
          const id = snap.data().gtmId;
          setGtmId(id);
          initializeGTM(id);
          setGtmLoaded(true);
        }
      } catch (err) {
        console.error("Error cargando ID del contenedor de GTM:", err);
      }
    }
    loadGTMConfig();
  }, []);

  useEffect(() => {
    const unsubscribeCore = subscribeToCoreServicesConfig();
    return () => {
      if (typeof unsubscribeCore === "function") unsubscribeCore();
    };
  }, []);

  useEffect(() => {
    const currentPath = location.pathname + location.search;

    if (gtmLoaded && gtmId && lastTrackedPath.current !== currentPath) {
      lastTrackedPath.current = currentPath;

      // Un ligero retraso permite que el componente secundario actualice document.title primero
      const timeoutId = setTimeout(() => {
        const userRole = profile?.isAdmin
          ? "admin"
          : (profile?.isClientAdmin ? "client_admin" : (profile?.isIHO ? "iho" : "user"));

        trackVirtualPageView(
          currentPath,
          document.title,
          {
            user_role: userRole,
            is_demo: user?.isDemo || false
          }
        );
      }, 150);

      return () => clearTimeout(timeoutId);
    }
  }, [location, gtmLoaded, gtmId, profile, user]);

  const isCommitteePublicRoute =
    location.pathname.startsWith("/comite-identidad") ||
    location.pathname.startsWith("/comite-reglas");

  if (!isCommitteePublicRoute && (loading || profileLoading)) {
    return <LoadingScreen />;
  }

  // Check if user has meaningful profile data (completed F1 or has key fields filled)
  const hasProfileData = profile?.completedAt || (profile?.nombres && profile?.fechaNacimiento && profile?.peso);

  // Determine default redirect based on user type and profile completion
  const getDefaultRedirect = () => {
    if (!user) return <Login />;
    if (isMedico && !isAdmin) return <Navigate to="/medico" replace />;
    if (user.isDemo) return <Navigate to="/dashboard" replace />;
    // While impersonating, always go to dashboard (admin viewing other user's data)
    if (isImpersonating) return <Navigate to="/dashboard" replace />;
    // If user has profile data or completed onboarding, go to dashboard
    if (hasProfileData || onboardingCompleted) return <Navigate to="/dashboard" replace />;
    // New user without onboarding → show onboarding
    if (!onboardingCompleted) return <Navigate to="/onboarding" replace />;
    return <Navigate to="/dashboard" replace />;
  };

  return (
    <>
      {!isCommitteePublicRoute && <ImpersonationBanner />}
      {!isCommitteePublicRoute && <DemoBanner />}
      {!isCommitteePublicRoute && user && !user.isDemo && !isImpersonating && (
        <div style={{ position: "relative" }}>
          <UserHeader />
        </div>
      )}
      <Routes>
        <Route path="/" element={getDefaultRedirect()} />
        <Route path="/login" element={user ? <Navigate to="/" replace /> : <Login defaultMode="login" />} />
        <Route path="/register" element={user ? <Navigate to="/" replace /> : <Login defaultMode="register" />} />
        <Route path="/join" element={user ? <Navigate to="/" replace /> : <Login defaultMode="register" />} />
        <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
        <Route path="/f1" element={<ProtectedRoute><FormF1 /></ProtectedRoute>} />
        <Route path="/f2" element={<ProtectedRoute><FormF2 /></ProtectedRoute>} />
        <Route path="/f3" element={<ProtectedRoute><FormF3 /></ProtectedRoute>} />
        <Route path="/f4" element={<ProtectedRoute><FormF4 /></ProtectedRoute>} />
        <Route path="/f5" element={<ProtectedRoute><FormF5 /></ProtectedRoute>} />
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/medicion/nueva" element={<ProtectedRoute><Measurement /></ProtectedRoute>} />
        <Route path="/mi-cuenta" element={<ProtectedRoute><MiCuenta /></ProtectedRoute>} />
        <Route path="/mi-cuenta/exportar" element={<ProtectedRoute><ExportPreview /></ProtectedRoute>} />
        <Route path="/recommendations" element={<ProtectedRoute><Recommendations /></ProtectedRoute>} />
        <Route path="/cliente" element={<ProtectedRoute><ClientConsole /></ProtectedRoute>} />
        <Route path="/iho" element={<ProtectedRoute><IHODashboard /></ProtectedRoute>} />
        <Route path="/medico" element={<MedicoRoute><DoctorDashboard /></MedicoRoute>} />
        <Route path="/changelog" element={<Changelog />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/comite-identidad" element={<CommitteeRulesVote />} />
        <Route path="/comite-reglas" element={<CommitteeRulesVote />} />
        <Route path="/dev/api-tester" element={<DevApiTester />} />
        <Route path="/admin/*" element={<AdminRoute><AdminPanel /></AdminRoute>} />
        <Route path="/forbidden" element={<Forbidden />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      {/* Consola de Auditoría FHIR para Administradores e Impersonación */}
      {(isAdmin || isImpersonating) && !user?.isDemo && (
        <>
          <FhirInspectorDrawer isOpen={fhirDrawerOpen} onClose={closeFhirDrawer} />
          {!isImpersonating && (
            <button
              onClick={openFhirDrawer}
              data-testid="floating-fhir-btn"
              title="Consola de Auditoría FHIR (HL7 R4 GCP)"
              className="fixed bottom-4 right-4 z-40 w-11 h-11 rounded-full bg-slate-900/90 hover:bg-slate-900 text-teal-400 border border-teal-500/40 shadow-xl flex items-center justify-center cursor-pointer transition-all hover:scale-110 active:scale-95 backdrop-blur-md"
              aria-label="Consola de Auditoría FHIR"
            >
              <Database size={18} />
            </button>
          )}
        </>
      )}
    </>
  );
}


function App() {
  return (
    <AuthProvider>
      <UserProfileProvider>
        <BrowserRouter>
          <AppContent />
        </BrowserRouter>
      </UserProfileProvider>
    </AuthProvider>
  );
}

export default App;
